import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import { fetchCustomerOrders } from "../lib/fetch-customer-orders";
import {
  fetchCustomerProfile,
  formatCustomerProfileError,
  type CustomerProfile,
} from "../lib/fetch-customer-profile";
import { fetchCustomerSavedAddresses } from "../lib/fetch-customer-saved-addresses";
import { fetchMe, formatMeFetchError } from "../lib/fetch-with-clerk";

function formatDob(isoDate: string): string {
  try {
    const d = new Date(`${isoDate}T12:00:00`);
    return d.toLocaleDateString(undefined, { dateStyle: "long" });
  } catch {
    return isoDate;
  }
}

function formatAddress(c: CustomerProfile): string | null {
  const parts = [
    c.defaultAddressLine1,
    [c.defaultCity, c.defaultState, c.defaultZip].filter(Boolean).join(", ") || null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return parts.join(" · ");
}

/** Last 8 chars of the customer UUID (dashes stripped) — same shorthand
 *  pattern as PR 1.6 OrderTracking. */
function shortCustomerId(id: string): string {
  return id.replace(/-/g, "").slice(-8).toUpperCase();
}

export function AccountLandingScreen() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
        <Header h1="Your account" lede="Profile, orders, addresses, and payment." />
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-amber-900">
            Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to use your account.
          </p>
        </div>
      </div>
    );
  }
  return <AccountLandingInner />;
}

function Header({ h1, lede }: { h1: string; lede: string }) {
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">Account</p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">{h1}</h1>
      <p className="text-sm text-[var(--text-secondary)]">{lede}</p>
    </header>
  );
}

const COPY_LABEL_RESET_MS = 2_000;

function AccountLandingInner() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [meError, setMeError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [orderCount, setOrderCount] = useState<number | null>(null);
  const [addressCount, setAddressCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setMeError(null);
      setProfileError(null);
      setProfile(null);
      setOrderCount(null);
      setAddressCount(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setMeError(null);
    setProfileError(null);
    void (async () => {
      try {
        const me = await fetchMe(getToken);
        if (cancelled) return;
        if (!me.user.roles.includes("customer") || !me.user.customerId) {
          setMeError(
            !me.user.roles.includes("customer")
              ? `This sign-in is not a shopper account (role: ${me.user.role}).`
              : "No customer profile is linked yet. Try signing out and back in.",
          );
          setProfile(null);
          setProfileError(null);
          return;
        }
        const customerId = me.user.customerId;
        /**
         * Fire profile + orders + addresses in parallel so the data-rich
         * Quick-links cards (orders count, addresses count, bank link
         * status) all populate from the same render. Each fetch's failure
         * degrades gracefully: missing counts hide the count hint instead
         * of showing a card-level error.
         */
        const [profileResult, ordersResult, addressesResult] = await Promise.allSettled([
          fetchCustomerProfile(getToken, customerId),
          fetchCustomerOrders(getToken),
          fetchCustomerSavedAddresses(getToken),
        ]);
        if (cancelled) return;
        if (profileResult.status === "fulfilled") {
          setProfile(profileResult.value);
          setProfileError(null);
        } else {
          setProfile(null);
          setProfileError(formatCustomerProfileError(profileResult.reason));
        }
        if (ordersResult.status === "fulfilled") {
          setOrderCount(ordersResult.value.orders.length);
        }
        if (addressesResult.status === "fulfilled") {
          setAddressCount(addressesResult.value.addresses.length);
        }
      } catch (e) {
        if (!cancelled) {
          setMeError(formatMeFetchError(e));
          setProfile(null);
          setProfileError(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    if (copiedAt == null) return;
    const id = window.setTimeout(() => setCopiedAt(null), COPY_LABEL_RESET_MS);
    return () => window.clearTimeout(id);
  }, [copiedAt]);

  async function onCopyCustomerId() {
    if (!profile) return;
    try {
      await navigator.clipboard.writeText(profile.id);
      setCopiedAt(Date.now());
    } catch {
      /* clipboard rejection — silently no-op */
    }
  }

  const copiedRecently = copiedAt != null;

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      <Header h1="Your account" lede="Profile, orders, addresses, and payment." />

      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">Sign in to view your account.</p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>

      <SignedIn>
        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-36 rounded-2xl bg-[var(--skeleton-bg)]" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="h-28 rounded-2xl bg-[var(--skeleton-bg)]" />
              <div className="h-28 rounded-2xl bg-[var(--skeleton-bg)]" />
              <div className="h-28 rounded-2xl bg-[var(--skeleton-bg)]" />
              <div className="h-28 rounded-2xl bg-[var(--skeleton-bg)]" />
            </div>
          </div>
        ) : meError ? (
          <div
            role="alert"
            className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
          >
            {meError}
          </div>
        ) : profileError ? (
          <div
            role="alert"
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            {profileError}
          </div>
        ) : profile ? (
          <>
            <section className="rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
              <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
                Profile
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="text-lg font-semibold text-[var(--text-primary)]">{profile.fullName}</p>
                <span className="text-xs font-medium text-[var(--text-muted)]">
                  ID #{shortCustomerId(profile.id)}
                </span>
                <button
                  type="button"
                  onClick={onCopyCustomerId}
                  aria-label="Copy full customer ID"
                  className="text-[10px] font-medium uppercase tracking-[.1em] text-[var(--text-muted)] transition-colors hover:text-[var(--green)]"
                >
                  {copiedRecently ? "Copied" : "Copy"}
                </button>
              </div>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-[var(--text-muted)]">Phone</dt>
                  <dd className="font-medium text-[var(--text-primary)]">{profile.phone}</dd>
                </div>
                {profile.email ? (
                  <div>
                    <dt className="text-[var(--text-muted)]">Email</dt>
                    <dd className="font-medium text-[var(--text-primary)]">{profile.email}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-[var(--text-muted)]">Date of birth</dt>
                  <dd className="font-medium text-[var(--text-primary)]">{formatDob(profile.dateOfBirth)}</dd>
                </div>
                <div>
                  <dt className="text-[var(--text-muted)]">Age verification</dt>
                  <dd className="font-medium text-[var(--text-primary)]">
                    {profile.isAgeVerified ? "Verified" : "Not verified"}
                  </dd>
                </div>
              </dl>
              {formatAddress(profile) ? (
                <div className="mt-4 border-t border-[var(--card-border)] pt-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
                    Default address
                  </p>
                  <p className="mt-1 text-sm text-[var(--text-primary)]">{formatAddress(profile)}</p>
                </div>
              ) : null}
            </section>

            <section className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
                Quick links
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <QuickLinkCard
                  to="/orders"
                  title="Orders"
                  hint={
                    orderCount == null
                      ? "Order history and tracking"
                      : orderCount === 0
                      ? "No orders yet"
                      : `${orderCount} ${orderCount === 1 ? "order" : "orders"}`
                  }
                />
                <QuickLinkCard
                  to="/account/addresses"
                  title="Saved addresses"
                  hint={
                    addressCount == null
                      ? "Save delivery addresses"
                      : addressCount === 0
                      ? "Save your first address"
                      : `${addressCount} ${addressCount === 1 ? "address" : "addresses"}`
                  }
                />
                <QuickLinkCard
                  to="/account/payment"
                  title="Payment"
                  hint={
                    profile.secrets.plaidItemConfigured || profile.bankAccountLast4
                      ? `Linked${profile.bankAccountLast4 ? ` · ····${profile.bankAccountLast4}` : ""}`
                      : "Link a bank account"
                  }
                />
                <QuickLinkCard
                  to="/account/identity"
                  title="Identity"
                  hint={profile.isAgeVerified ? "Verified" : "Verify your age"}
                />
              </div>
            </section>
          </>
        ) : null}
      </SignedIn>

      <div className="border-t border-[var(--card-border)] pt-6">
        <Link
          to="/"
          className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
        >
          ← Back to marketplace
        </Link>
      </div>
    </div>
  );
}

function QuickLinkCard({ to, title, hint }: { to: string; title: string; hint: string }) {
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd transition-all hover:border-[var(--green)] hover:shadow-lg"
    >
      <span className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--green)]">
        {title}
      </span>
      <span className="mt-1 text-xs text-[var(--text-muted)]">{hint}</span>
    </Link>
  );
}
